import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateChild } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectMeFromReducer } from '../store/selectors/user.selectors';
import { SharedAppState } from '../store/app.states';
import * as userActions from '../store/actions/user.actions';
import { AuthenticationService } from '../service/authentification.service';

@Injectable({
  providedIn: 'root'
})
export class ProfileStoreGuard implements CanActivate, CanActivateChild {
  private userDataLoaded = false;

  constructor(
    private store: Store<SharedAppState>,
    private router: Router,
    private _authentificationService: AuthenticationService
  ) { }

  /** Réinitialise le cache après logout. */
  public resetUserDataLoaded(): void {
    this.userDataLoaded = false;
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkUserInformation();
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkUserInformation();
  }

  private checkUserInformation(): Observable<boolean | UrlTree> {
    if (this.userDataLoaded) {
      return this.store.select(selectMeFromReducer).pipe(
        take(1),
        map(store => this.evaluateProfile(store.me)),
      );
    }

    // Chaîner /me dans le flux du guard : évite le hang quand me reste null
    // (ex. filter(me !== null) qui ne se résolvait jamais après un échec /me).
    return this._authentificationService.me().pipe(
      map((authResponse) => {
        this.store.dispatch(userActions.GetUserSuccess(authResponse));
        this.userDataLoaded = true;
        return this.evaluateProfile(authResponse);
      }),
      catchError(() => {
        this.store.dispatch(userActions.GetUserFailure());
        this.userDataLoaded = false;
        // Ne pas bloquer le dashboard : laisser la route s'activer (l'API métier gère l'auth).
        return of(true);
      }),
    );
  }

  private evaluateProfile(me: any): boolean {
    if (!me) {
      return true;
    }
    if (!me.already_onboarded) {
      return true;
    }
    const user = me.user;
    const profileComplete = !!(
      user
      && user.birthdate
      && user.gender
      && user.job_situation != null
      && (
        (user.business && user.job)
        || user.job_situation === 'retired'
        || user.job_situation === 'student'
      )
    );
    if (!profileComplete) {
      this.router.navigateByUrl('/missing-informations');
      return false;
    }
    return true;
  }
}
